import React from "react";
import { FiTrash2, FiAlertTriangle, FiX } from "react-icons/fi";
import { axiosdelete, axiospost, axiosput } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import Modal from "react-modal";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button";
import { Card, CardContent, CardFooter } from "../../components/ui/Card";
import { FormInput } from "../../components/ui/FormInput";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/Alert";
import { Formik, Form } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import * as Yup from "yup";

/**
 * Modern delete button component for O-Level results
 * Features confirmation modal with type-to-confirm pattern for safety
 *
 * @param {Object} props - Component props
 * @param {Object} props.modules - O-Level result object containing _id, subject, scored (grade)
 */
const BtnDeleteOLevel = ({ modules }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const queryClient = useQueryClient();

  const validationSchema = Yup.object().shape({
    confirmText: Yup.string()
      .required("Please type 'Delete' to confirm")
      .test("match-delete", "You must type exactly 'Delete' to confirm", (value) => value === "Delete"),
  });

  const _save = async (values, { setSubmitting }) => {
    try {
      let req = await axiosdelete(`ms_applicant_sitting/${modules?._id}`);

      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { data: result } = req;
      if (result && result.type === "success") {
        const { message, type } = result;
        swal(type, message);
        queryClient.invalidateQueries({ queryKey: ["oLevelSitting"] });
        queryClient.invalidateQueries({ queryKey: ["oLevelResult"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
        _close();
      } else {
        throw new Error(result.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    } finally {
      setSubmitting(false);
    }
  };

  const _close = () => {
    setshowmodal(false);
  };

  const _show = () => {
    setshowmodal(true);
  };

  return (
    <div>
      <AnimatePresence>
        {showmodal && (
          <Modal
            isOpen={showmodal}
            onRequestClose={_close}
            className="flex items-center justify-center outline-none"
            overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4"
            closeTimeoutMS={200}
            ariaHideApp={false}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="w-full min-w-[60%] max-w-2xl"
            >
              <Card variant="danger" padding="none" className="shadow-2xl border-2 border-red-300">
                {/* Warning Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-6 text-white rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ rotate: 0 }}
                      animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm"
                    >
                      <FiAlertTriangle className="h-6 w-6" />
                    </motion.div>
                    <div>
                      <h2 className="text-2xl font-bold">Delete O-Level Result</h2>
                      <p className="text-red-100 text-sm mt-1">This action cannot be undone</p>
                    </div>
                  </div>
                </div>

                {/* Form Content */}
                <CardContent className="p-6">
                  {/* Danger Alert */}
                  <Alert variant="danger" className="mb-6">
                    <AlertTitle>Warning: Permanent Deletion</AlertTitle>
                    <AlertDescription>
                      You are about to permanently delete the following O-Level result:
                    </AlertDescription>
                  </Alert>

                  {/* Result Details Card */}
                  {modules && (
                    <div className="mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600 flex-shrink-0">
                          <FiTrash2 className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-neutral-500 mb-1">Subject & Grade</p>
                          <p className="text-lg font-semibold text-neutral-900">
                            {modules.subject || "Unknown Subject"} - {modules.scored || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* What will be deleted */}
                  <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-semibold text-red-900 mb-2">This will delete:</p>
                    <ul className="space-y-1 text-sm text-red-800">
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Subject and grade information
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Examination sitting details
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Associated records from your application
                      </li>
                    </ul>
                  </div>

                  {/* Confirmation Form */}
                  <Formik
                    initialValues={{ confirmText: "" }}
                    validationSchema={validationSchema}
                    onSubmit={_save}
                  >
                    {({ isSubmitting, values }) => (
                      <Form className="space-y-4">
                        <div>
                          <p className="text-sm text-neutral-700 mb-4">
                            To confirm deletion, please type{" "}
                            <span className="font-mono font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                              Delete
                            </span>{" "}
                            in the field below:
                          </p>
                          <FormInput
                            name="confirmText"
                            label="Type 'Delete' to confirm"
                            type="text"
                            leftIcon={<FiAlertTriangle />}
                            required
                            className="mb-0"
                          />
                        </div>

                        {/* Action Buttons */}
                        <CardFooter className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-6 border-t border-red-200 px-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="lg"
                            onClick={_close}
                            leftIcon={<FiX className="h-5 w-5" />}
                            className="w-full sm:w-auto"
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="danger"
                            size="lg"
                            loading={isSubmitting}
                            leftIcon={<FiTrash2 className="h-5 w-5" />}
                            className="w-full sm:w-auto"
                            disabled={values.confirmText !== "Delete"}
                          >
                            {isSubmitting ? "Deleting..." : "Delete Result"}
                          </Button>
                        </CardFooter>
                      </Form>
                    )}
                  </Formik>
                </CardContent>
              </Card>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Trigger Button */}
      <div>
        <Button
          variant="danger"
          size="icon"
          onClick={_show}
          className="rounded-full hover:scale-110 transition-transform duration-200"
          aria-label={modules ? `Delete ${modules.subject} result` : "Delete O-Level result"}
        >
          <FiTrash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default BtnDeleteOLevel;
